import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { CORE_ROLES, ROLE_METADATA, UserRole } from '@/components/authnz/types/rbac';
import type { PrototypeAction, PrototypeRow } from './types';
import { DiagnosticBlock, GrantChips, LifecycleBadge } from './parts';

export const VARIANT_A_NAME = 'Dense grant table';

export function VariantA({
  rows,
  onAction,
}: {
  rows: PrototypeRow[];
  onAction: (action: PrototypeAction) => void;
  stepUpOk: boolean;
}) {
  const [query, setQuery] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.TUTOR);
  const [open, setOpen] = useState<PrototypeRow | null>(null);

  const shown = rows.filter((row) => row.emailKey.includes(query.toLowerCase()));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        One row per email. No status column. No pre_ twins. Diagnostic is a sheet. No email edit.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <Input
          placeholder="Search email key"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="max-w-xs"
        />
        <Input
          placeholder="Assign to email"
          value={inviteEmail}
          onChange={(event) => setInviteEmail(event.target.value)}
          className="max-w-xs"
        />
        <select
          className="h-10 rounded-md border border-input bg-background px-2 text-sm"
          value={inviteRole}
          onChange={(event) => setInviteRole(event.target.value as UserRole)}
        >
          {CORE_ROLES.map((role) => (
            <option key={role} value={role}>
              {ROLE_METADATA[role].displayName}
            </option>
          ))}
        </select>
        <Button
          type="button"
          onClick={() => {
            if (inviteEmail.trim()) {
              onAction({
                type: 'invite',
                emailKey: inviteEmail.trim().toLowerCase(),
                role: inviteRole,
              });
              setInviteEmail('');
            }
          }}
        >
          Assign grant
        </Button>
        <Button type="button" variant="outline">
          Refresh (on demand, no 10s poll)
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email key</TableHead>
            <TableHead>Lifecycle</TableHead>
            <TableHead>Grants</TableHead>
            <TableHead>Last sign-in</TableHead>
            <TableHead>Uids</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map((row) => (
            <TableRow key={row.emailKey}>
              <TableCell className="font-medium">{row.emailKey}</TableCell>
              <TableCell>
                <LifecycleBadge row={row} />
              </TableCell>
              <TableCell>
                <GrantChips
                  grants={row.grants}
                  onRevokeAdmin={(role) =>
                    onAction({ type: 'revoke-admin', emailKey: row.emailKey, role })
                  }
                />
              </TableCell>
              <TableCell className="text-muted-foreground">
                {row.lastSignIn ?? '—'}
              </TableCell>
              <TableCell>{row.knownUids}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="outline" onClick={() => setOpen(row)}>
                    Why?
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      onAction({ type: 'copy-link', emailKey: row.emailKey })
                    }
                  >
                    Copy link
                  </Button>
                  {row.lifecycle === 'deleted' ? (
                    <Button
                      size="sm"
                      onClick={() => onAction({ type: 'restore', emailKey: row.emailKey })}
                    >
                      Restore
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => onAction({ type: 'delete', emailKey: row.emailKey })}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Sheet open={Boolean(open)} onOpenChange={() => setOpen(null)}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Why can this email not get in?</SheetTitle>
          </SheetHeader>
          {open ? <DiagnosticBlock row={open} /> : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
